/**
 * Phase 13 Metrics Dashboard — useDviLearningDashboard
 *
 * Manager-only summary RPC + realtime invalidation on this shop's confirmed
 * findings.
 */

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type RecommendationFilter = 'recommended' | 'not_recommended' | null;
export type OcrBand = 'low' | 'mid' | 'high' | null;

export interface DviLearningDashboardSummary {
  total_confirmed_findings: number;
  total_analyzed_media: number;
  total_comparisons: number;
  // B-19 (P-05): these came from the platform-wide confidence aggregate, which a shop may not read; the server
  // returns null (unavailable) for them. Never render null as 0.
  components_with_min_samples: number | null;
  components_in_suggestion: number | null;
  components_in_prefill: number | null;
  components_in_job_suggestion: number | null;
  components_automation_ready: number | null;
  avg_component_accuracy: number | null;
  avg_severity_accuracy: number | null;
  avg_recommendation_accuracy: number | null;
  accepted_count: number | null;
  edited_count: number | null;
  rejected_count: number | null;
  // Shop-scoped averages: null when the shop has no comparisons in range.
  ocr_success_rate: number | null;
  avg_ocr_confidence: number | null;
  avg_vision_confidence: number | null;
  ocr_supported_match_score_avg: number | null;
  ocr_unsupported_match_score_avg: number | null;
  ocr_lift_pct: number | null;
  // Phase 14 — behavior performance counts
  suggestions_shown_count?: number;
  suggestions_accepted_count?: number;
  prefills_accepted_count?: number;
  prefills_overridden_count?: number;
  job_drafts_generated_count?: number;
  job_drafts_accepted_count?: number;
  job_drafts_edited_count?: number;
  job_drafts_rejected_count?: number;
}

export function useDviLearningDashboard(
  shopId: string | null,
  rangeDays: number = 30,
  recommendationFilter: RecommendationFilter = null,
  ocrBand: OcrBand = null,
) {
  const qc = useQueryClient();
  const queryKey = ['dvi-learning-dashboard', shopId, rangeDays, recommendationFilter, ocrBand];

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<DviLearningDashboardSummary | null> => {
      const { data, error } = await supabase.rpc('get_dvi_learning_dashboard_summary', {
        p_shop_id: shopId,
        p_range_days: rangeDays,
        p_recommendation_filter: recommendationFilter,
        p_ocr_band: ocrBand,
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return (row as DviLearningDashboardSummary | null) ?? null;
    },
    enabled: !!shopId,
    staleTime: 30 * 1000,
  });

  // Debounced realtime invalidation
  useEffect(() => {
    if (!shopId) return;
    let timeout: ReturnType<typeof setTimeout> | null = null;
    const invalidate = () => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['dvi-learning-dashboard'] });
        qc.invalidateQueries({ queryKey: ['dvi-stage-readiness'] });
      }, 2000);
    };
    // B-19 (P-05): no subscription to the platform-wide dvi_component_confidence table. Shops may no longer read it
    // (internal admins only), and the summary no longer returns values derived from it. The summary's remaining
    // sources are this shop's findings, media, comparisons and ledger; only confirmed findings are subscribed, and
    // staleTime covers the rest. Do not add a platform-wide subscription here.
    const ch = supabase
      .channel(`dvi-learning-${shopId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'ticket_dvi_confirmed_findings', filter: `shop_id=eq.${shopId}` },
        invalidate,
      )
      .subscribe((status, err) => {
        // Chronomancer R-RT-6: surface channel drops so a stale learning
        // dashboard is diagnosable. React Query staleTime covers the soft
        // fallback, but operators want to see connection issues surfaced.
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          console.error('[useDviLearningDashboard] realtime channel status:', status, err);
        }
      });
    return () => {
      if (timeout) clearTimeout(timeout);
      supabase.removeChannel(ch);
    };
  }, [shopId, qc]);

  return query;
}
